import classes from "./TopBar.module.css";
import esperiLogo from "../../images/esperiLogo.png";
import { useUserContext } from "../../contexts/UserContext";
import { useNavigate, useLocation, matchPath } from "react-router-dom";

import { CgProfile } from "react-icons/cg";
import { useState, useEffect, useRef } from "react";
import FormPageChangeConfirm from "../Form/FormPageChangeConfirm/FormPageChangeConfirm";
import { useRoutesContext } from "../../contexts/RoutesContext";
import { useCurrentDataContext } from "../../contexts/CurrentDataContext";
import { useOriginalDataContext } from "../../contexts/OriginalDataContext";
import { SignOutButton } from "../ADLogin/SignOutButton";
import config from "../../config/config";

const TopBar = () => {
  const { user, setUser } = useUserContext();

  const { currentData, setCurrentData } = useCurrentDataContext();
  const { setOriginalData } = useOriginalDataContext();

  const { routes } = useRoutesContext();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [confirmNeeded, setConfirmNeeded] = useState<boolean>(false);
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);


  const handleLogoClick = () => {
    // If user is doctor, return to doctor's main view.
    // Else If user is currently in /form/:empId path
    // show modal to ask if user wants to save current form before leaving
    // Admins should go to their normal view (doctor or main), not admin view
    if (user?.isDoctor) {
      // Clear current and original datas
      setOriginalData(undefined);
      setCurrentData(undefined);
      navigate(user ? `/${routes.doctorMain}` : "/");
    } else if (
      matchPath(`/${routes.form}/:empId/*`, pathname) &&
      currentData?.formStatus !== "waiting" &&
      currentData?.formId
    ) {
      setConfirmNeeded(true);
    } else {
      // Clear current and original datas
      setOriginalData(undefined);
      setCurrentData(undefined);
      navigate(user ? `/${routes.main}` : "/");
    }
  };

  // Handle toggling between doctor/regular user mode
  const handleRoleToggle = () => {
    if (!user) return;

    // Clear context data
    setOriginalData(undefined);
    setCurrentData(undefined);

    // Toggle the doctor role
    const newIsDoctor = !user.isDoctor;
    setUser({ ...user, isDoctor: newIsDoctor });

    // Navigate to the appropriate home page based on the new role
    if (newIsDoctor) {
      navigate(`/${routes.doctorMain}`);
    } else {
      navigate(`/${routes.main}`);
    }
  };

  // Handle user name click to toggle dropdown
  const handleUserNameClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // Handle navigation to admin view
  const handleAdminNavigation = () => {
    if (!user) return;
    
    // Clear context data
    setOriginalData(undefined);
    setCurrentData(undefined);
    
    // Navigate to admin view
    navigate(`/${routes.admin}`);
    setShowUserDropdown(false);
  };

  // Handle navigation to main page (or doctor main if user is doctor)
  const handleMainNavigation = () => {
    if (!user) return;
    
    // Clear context data
    setOriginalData(undefined);
    setCurrentData(undefined);
    
    // Navigate to appropriate main page based on user role
    if (user.isDoctor) {
      navigate(`/${routes.doctorMain}`);
    } else {
      navigate(`/${routes.main}`);
    }
    setShowUserDropdown(false);
  };

  // Check if user has any navigation options (units, isDoctor, or isAdmin)
  const hasNavigationOptions = user && (
    (user.units && user.units.length > 0) ||
    user.isDoctor ||
    user.isAdmin
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  return (
    <div className={classes.topBarBackground}>
      <div className={classes.topBarContainer}>
        <div
          className={classes.logoContainer}
          onClick={() => handleLogoClick()}
        >
          <img src={esperiLogo} alt="" />
        </div>
        {
          // If not on production server, show isDoctor button to switch between foreman / doctor views
          config?.serverUrl ===
            "https://fadevesperimedication.azurewebsites.net" ||
          config?.serverUrl === "http://localhost:7071" ? (
            <button onClick={handleRoleToggle}> Lääkäri / Esihenkilö</button>
          ) : null
        }
        {user && (
          <div className={classes.loggedInContainer} ref={dropdownRef}>
            <CgProfile className={classes.userCircle} />
            <div
              className={`${classes.userName} ${hasNavigationOptions ? classes.userNameClickable : ""}`}
              onClick={hasNavigationOptions ? handleUserNameClick : undefined}
            >
              {!user?.firstName && !user?.lastName && user?.isDoctor
                ? "Lääkäri"
                : `${user?.firstName} ${user?.lastName}`}
            </div>
            {showUserDropdown && (
              <div className={classes.userDropdown}>
                {user?.units && user.units.length > 0 && (
                  <div
                    className={classes.dropdownItem}
                    onClick={handleMainNavigation}
                  >
                    Etusivu
                  </div>
                )}
                {user?.isAdmin && (
                  <div
                    className={classes.dropdownItem}
                    onClick={handleAdminNavigation}
                  >
                    Hallintapaneeli
                  </div>
                )}
              </div>
            )}
            <div className={classes.loggedInDivider} />
            <SignOutButton />
          </div>
        )}
        {confirmNeeded && (
          <FormPageChangeConfirm setConfirmNeeded={setConfirmNeeded} />
        )}
      </div>
    </div>
  );
};

export default TopBar;
